import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { ControllersProvider, useControllers } from "./state/controllers";
import { useProjectStore } from "./state/projectStore";
import { AnalysisPanel } from "./components/AnalysisPanel";
import { ScoreSection } from "./components/ScoreSection";
import { AboutSection } from "./components/AboutSection";
import { PlaybackControls } from "./components/PlaybackControls";
import { SongInput } from "./components/SongInput";
import { PlayDock } from "./components/PlayDock";
import { MyLinks } from "./components/MyLinks";
import { Logo } from "./components/Logo";
import { useT } from "./i18n";
import { useUi } from "./state/ui";
import { useAuth } from "./state/auth";
import { BandIcon } from "./components/icons";
import { bandRoute, openBand } from "./core/bandRoute";
import { Toast } from "./components/Toast";
import { bindKeyToSong, useKeyStore } from "./state/keyStore";
import { bindStyleToSong } from "./state/styleStore";
import { useToast } from "./state/toast";
import { settingLabel } from "./components/keyText";
import { videoIdFromLocation } from "./core/songUrl";

// The tab title follows the open song — it also names the file when the print dialog saves a PDF.
// The page that was loaded keeps the server's SEO title; songs opened later get the same "… 기타 코드" form.
const DEFAULT_TITLE = "Tutti — YouTube·오디오 코드 분석 / 기타 코드·타브 악보 만들기";
const INITIAL = { title: document.title, videoId: videoIdFromLocation(location.pathname, location.search) };

const BandsPage = lazy(() => import("./components/BandsPage").then((m) => ({ default: m.BandsPage })));
// the band workspace ships together with its server API; production builds hide it until then
// (dev server: always on · VITE_BANDS=1 npm run build: on)
const BANDS = import.meta.env.DEV || import.meta.env.VITE_BANDS === "1";

function Shell() {
  const c = useControllers();
  const sourceKind = useProjectStore((s) => s.source.kind);
  const hasChart = useProjectStore((s) => s.chartMeasures.length > 0);
  const linksPage = useUi((s) => s.linksPage);
  const setLinksPage = useUi((s) => s.setLinksPage);
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [workspaceRoute, setWorkspaceRoute] = useState(() => (BANDS ? bandRoute(location.pathname) : null));
  const scrollRef = useRef<HTMLElement>(null);

  // top-left logo → back to the initial analysis (input) screen
  const goHome = () => {
    c.song.pause(); c.youtube.stop(); c.metro.pause();
    useProjectStore.getState().reset();
    setLinksPage(false);
    useUi.getState().setInputOpen(false);
    scrollRef.current?.scrollTo({ top: 0 });
    if (location.pathname !== "/") {
      history.pushState(null, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  };

  useEffect(() => {
    const changed = () => {
      const next = BANDS ? bandRoute(location.pathname) : null;
      setWorkspaceRoute(next);
      if (next) {
        c.song.pause(); c.youtube.stop(); c.metro.pause();
        useUi.getState().setLinksPage(false);
        document.title = "내 밴드 · Tutti";
      }
      scrollRef.current?.scrollTo({ top: 0 });
    };
    changed();
    window.addEventListener("popstate", changed);
    return () => window.removeEventListener("popstate", changed);
  }, [c]);

  useEffect(() => {
    // no demo song — the app starts at the input screen until a file/YouTube link is analyzed
    c.cursorLoop.start();
    useAuth.getState().refresh();   // restore login session (and its saved links) if any
    return () => c.cursorLoop.stop();
  }, [c]);

  const source = useProjectStore((s) => s.source);
  const displayTitle = useProjectStore((s) => s.displayTitle);
  const songKey = useProjectStore((s) => s.analysis?.key);
  useEffect(() => {
    if (workspaceRoute) return;   // the band space sets its own title
    if (source.kind === "default") { document.title = INITIAL.videoId ? DEFAULT_TITLE : INITIAL.title; return; }
    if (source.kind === "youtube" && source.videoId === INITIAL.videoId) { document.title = INITIAL.title; return; }
    const key = songKey ? ` (${songKey.tonic} ${songKey.quality === "minor" ? "단조" : "장조"})` : "";
    document.title = displayTitle ? `${displayTitle} 기타 코드${key} | Tutti` : DEFAULT_TITLE;
  }, [source, displayTitle, songKey, workspaceRoute]);

  // each loaded song gets its key/capo (shared link first, else this device's memory); say when it was restored
  const tRef = useRef(t);
  tRef.current = t;
  useEffect(() => bindKeyToSong((k) => {
    const key = useProjectStore.getState().analysis?.key;
    if (!key) return;
    const tr = tRef.current;
    useToast.getState().show(tr("key.restored", { s: settingLabel(tr, { tonic: key.tonic, quality: key.quality }, k) }),
      { label: tr("key.reset"), run: () => useKeyStore.getState().reset() });
  }), []);
  useEffect(() => bindStyleToSong(), []);

  return (
    <div className={"app" + (hasChart && !workspaceRoute ? " has-dock" : "")}>
      <header className={"topbar" + (scrolled ? " floating" : "")}>
        <button className="brand" onClick={goHome} aria-label={t("brand.home")}>
          <Logo />
          <span className="name"><em>Tutti</em></span>
          <span className="tagline">{t("brand.tagline")}</span>
        </button>
        <div className="topbar-spacer" />
        {BANDS && (
          <button className={"band-nav" + (workspaceRoute ? " active" : "")} onClick={() => openBand()} aria-label={t("bands.nav")}>
            <BandIcon size={18} /><span>{t("bands.nav")}</span>
          </button>
        )}
        <PlaybackControls bandMode={!!workspaceRoute} />
      </header>
      <main className="scroll-area" ref={scrollRef}
        onScroll={(e) => setScrolled((e.target as HTMLElement).scrollTop > 80)}>
        {workspaceRoute && <Suspense fallback={<div className="content" role="status">밴드 공간을 불러오는 중…</div>}><BandsPage route={workspaceRoute} /></Suspense>}
        <div className="content" hidden={!!workspaceRoute}>
          <SongInput />
          <AnalysisPanel />
          <ScoreSection />
          <section className={"section yt-wrap" + (sourceKind === "youtube" ? "" : " hidden")}>
            {/* below the score: the sheet owns the first screen; the player stays visible on the page */}
            <h2>YouTube</h2>
            <div className="yt-frame"><div id="yt-host" /></div>
          </section>
          <AboutSection />
          <footer className="app-foot">
            <div className="foot-brand"><Logo size={18} /><span>Tutti</span></div>
            <nav className="foot-nav">
              <a href="/popular">{t("footer.popular")}</a>
              <a href="/about">{t("footer.about")}</a>
              <a href="/contact">{t("footer.contact")}</a>
              <a href="/terms">{t("footer.terms")}</a>
              <a href="/privacy">{t("footer.privacy")}</a>
            </nav>
            <div className="foot-copy">© 2026 Tutti · tutti-music.net</div>
          </footer>
        </div>
      </main>
      {hasChart && !linksPage && !workspaceRoute && <PlayDock />}
      {linksPage && <MyLinks onClose={() => setLinksPage(false)} />}
      <Toast />
    </div>
  );
}

export default function App() {
  return (
    <ControllersProvider>
      <Shell />
    </ControllersProvider>
  );
}
